// Lo que te toca, dentro del chat (S10): encima del compositor, una tarjeta por cada cosa que el agente
// necesita de Pau — su plan por aprobar, una pregunta, «¿lo integro?», un cierre atascado o mensajes que no le
// llegaron. Se actúa donde se lee (antes solo existía en el panel lateral). 27-10 (ADR D-036; Pau: «quítame estas
// tarjetas… me llenan el chat de permisos»): ya no hay tarjeta de permiso; lo que es de Pau se lo pide el agente
// hablando en el chat.
// Los datos salen del Expediente (GET /agentes/:id/expediente), que el socket mantiene al día.
import { ChevronDown, KeyRound, MessageCircleQuestion, RotateCcw, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { oficinaApi } from '../api';
import { mensajeError } from '../errores';
import { useOficinaStore } from '../store';
import { LIMITES, type ContratadoDTO, type ExpedienteDTO, type PreguntaDTO } from '../compartido/vocabulario';
import { AreaTexto, Aviso, Boton } from '../ui';

/** Enviar una respuesta de Pau y recargar el expediente; el error, en cristiano. */
export function useAccion(agentId: string) {
  const recargar = useOficinaStore((s) => s.cargarExpediente);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hacer = async (accion: () => Promise<unknown>): Promise<boolean> => {
    setEnviando(true);
    setError(null);
    try {
      await accion();
      await recargar(agentId);
      return true;
    } catch (e) {
      setError(mensajeError(e));
      return false;
    } finally {
      setEnviando(false);
    }
  };
  return { enviando, error, hacer };
}

export function FranjaAccion({ contratado: c, expediente: e }: { contratado: ContratadoDTO; expediente: ExpedienteDTO | undefined }) {
  if (!e) return null;
  // 29-10 (Pau: «que los agentes no me envíen esta mierda de tarjetas, nunca lo he pedido»): fuera el plan, la pregunta
  // del agente, el «¿Integro?» y el «No ha podido terminar». Al plan y a la pregunta les contesta escribiendo (le llegan
  // como mensaje del chat); un cierre que no sale ya no espera a Pau. Quedan solo los mensajes suyos que no llegaron
  // (su único «Reenviar») y las preguntas que hace la oficina, que aún no tienen otra forma de contestarse.
  const tarjetas: ReactNode[] = [];
  if (e.sinEntregar.length) tarjetas.push(<SinEntregar key="sin-entregar" contratado={c} expediente={e} />);
  if (e.pregunta?.deLaOficina) tarjetas.push(<Pregunta key={e.pregunta.id} agentId={c.agentId} p={e.pregunta} />);
  if (!tarjetas.length) return null;
  // 03-11 (Apple): lo que te toca, como tarjetas flotando sobre el compositor (sin franja de borde a borde).
  return <div className="max-h-[45%] shrink-0 space-y-2 overflow-y-auto bg-oficina-elevada px-4 pb-1 pt-2 sm:px-6">{tarjetas}</div>;
}

/**
 * Plegada por defecto: una línea con lo que pide y una flecha (29-09: «estas boxes me ocupan mucho espacio y
 * hacen mucho ruido; no puedo minimizarlas»). Al pulsarla se abre con el detalle y los botones.
 * `rapidos` (09-10, Pau aprobó la maqueta «botones ya a la vista»): la respuesta de un clic en la propia línea
 * plegada («Revisar plan»); el detalle sigue plegado. La pregunta usa TarjetaAbierta (10-10).
 */
function Tarjeta({ icono: Icono, titulo, tono = 'accion', rapidos, children }: { icono: typeof KeyRound; titulo: ReactNode; tono?: 'accion' | 'aviso'; rapidos?: ReactNode; children?: ReactNode }) {
  const [abierta, setAbierta] = useState(false);
  return (
    <section
      className={cn(
        'max-w-[900px] rounded-o-l border bg-oficina-control',
        tono === 'aviso' ? 'border-oficina-bloqueado/40' : 'border-oficina-te-necesita/40',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2">
        <button
          type="button"
          onClick={() => setAbierta((x) => !x)}
          aria-expanded={abierta}
          className="flex min-w-[min(100%,240px)] flex-1 items-center gap-2 rounded-o-m px-4 py-2.5 text-left text-o-cuerpo font-semibold leading-snug text-oficina-texto outline-none hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
        >
          <Icono className={cn('h-4 w-4 shrink-0', tono === 'aviso' ? 'text-oficina-bloqueado' : 'text-oficina-te-necesita')} aria-hidden />
          <span className={cn('min-w-0 flex-1', !abierta && 'truncate')}>{titulo}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-oficina-texto3 transition-transform', abierta && 'rotate-180')} aria-hidden />
        </button>
        {!abierta && rapidos ? <div className="flex flex-col items-end gap-1 pb-2 pl-10 pr-3 sm:pb-0 sm:pl-0" data-testid="rapidos">{rapidos}</div> : null}
      </div>
      {abierta && children ? <div className="space-y-2 px-4 pb-3 pl-10">{children}</div> : null}
    </section>
  );
}

/**
 * 10-10 (informe de espacio, puntos 6 y 7; Pau: «dale a todo menos la 19»): para el permiso y la pregunta, lo que
 * Pau decide no cabe en una línea cortada. La petición se lee entera (hasta 3 líneas) en su fila y las respuestas
 * van debajo, siempre a la vista. Lo que sigue plegado (29-09: «estas boxes me ocupan mucho espacio») es solo la
 * explicación larga: por qué, riesgo, cómo se deshace, contexto. Al desplegarla, la petición también deja de cortarse.
 */
function TarjetaAbierta({ icono: Icono, encabezado, titulo, pie, children }: {
  icono: typeof KeyRound;
  encabezado?: string;
  titulo: ReactNode;
  /** La fila de respuestas; recibe el botón «Ver detalles» para colocarlo donde toque (no roba ancho a la petición). */
  pie: (detalles: ReactNode) => ReactNode;
  children?: ReactNode;
}) {
  const [abierta, setAbierta] = useState(false);
  const detalles = children ? (
    <button
      type="button"
      onClick={() => setAbierta((x) => !x)}
      aria-expanded={abierta}
      className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md px-1.5 text-o-sub text-oficina-texto3 outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
    >
      {t(abierta ? 'oficinaV2.accion.ocultarDetalles' : 'oficinaV2.accion.verDetalles')}
      <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', abierta && 'rotate-180')} aria-hidden />
    </button>
  ) : null;
  return (
    <section className="max-w-[900px] rounded-o-l border border-oficina-te-necesita/40 bg-oficina-control">
      <div className="flex items-start gap-2 px-4 pt-3">
        <Icono className="mt-[3px] h-4 w-4 shrink-0 text-oficina-te-necesita" aria-hidden />
        <div className="min-w-0 flex-1">
          {encabezado ? <p className="text-o-sub font-semibold text-oficina-texto2">{encabezado}</p> : null}
          <p className={cn('break-words text-o-cuerpo font-semibold leading-snug text-oficina-texto', !abierta && 'line-clamp-3')}>{titulo}</p>
        </div>
      </div>
      {abierta && children ? <div className="space-y-2 px-4 pl-10 pt-2">{children}</div> : null}
      <div className="px-4 pb-3 pl-10 pt-2.5">{pie(detalles)}</div>
    </section>
  );
}

function Detalle({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <p className="text-o-texto text-oficina-texto2">
      <span className="text-oficina-texto3">{titulo} </span>
      {children}
    </p>
  );
}


// ── Plan propuesto: resumen aquí; el plan entero, en su ventana ──


// ── Pregunta ──

function Pregunta({ agentId, p }: { agentId: string; p: PreguntaDTO }) {
  const { enviando, error, hacer } = useAccion(agentId);
  const [otra, setOtra] = useState(p.opciones.length === 0);
  const [texto, setTexto] = useState('');
  const responder = (cuerpo: { opcionId?: string; texto?: string }) => void hacer(() => oficinaApi.responderDecision(agentId, { preguntaId: p.id, ...cuerpo }));
  // Todas las opciones a la vista; la recomendada, primero (maqueta aprobada del punto 7).
  const opciones = [...p.opciones].sort((x, y) => Number(y.id === p.recomendacion) - Number(x.id === p.recomendacion));
  const detalle = (
    <>
      {p.contexto ? <p className="text-o-texto text-oficina-texto2">{p.contexto}</p> : null}
      <p className={cn('text-o-sub', p.bloquea ? 'text-oficina-bloqueado' : 'text-oficina-texto3')}>
        {t(p.bloquea ? 'oficinaV2.accion.bloquea' : 'oficinaV2.accion.noBloquea')}
      </p>
      {p.porQueRecomienda ? <Detalle titulo={t('oficinaV2.accion.porQueRecomienda')}>{p.porQueRecomienda}</Detalle> : null}
    </>
  );
  const pie = (detalles: ReactNode) => (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2" data-testid="respuestas">
        {opciones.map((o) => {
          const recomendada = o.id === p.recomendacion;
          return (
            <Boton
              key={o.id}
              variante={recomendada ? 'primario' : 'secundario'}
              tamano="sm"
              disabled={enviando}
              onClick={() => responder({ opcionId: o.id })}
              className="h-auto min-h-9 max-w-full whitespace-normal py-1.5 text-left leading-snug"
            >
              <span>
                {o.texto}
                {recomendada ? <span className="font-medium opacity-80">{` · ${t('oficinaV2.accion.recomendado')}`}</span> : null}
              </span>
            </Boton>
          );
        })}
        <span className="ml-auto flex items-center gap-1">
          {detalles}
          {!otra ? (
            <Boton variante="fantasma" tamano="sm" onClick={() => setOtra(true)}>
              {t('oficinaV2.accion.oEscribele')}
            </Boton>
          ) : null}
        </span>
      </div>
      {otra ? (
        <div className="flex items-end gap-2">
          <AreaTexto
            rows={1}
            value={texto}
            maxLength={LIMITES.texto}
            onChange={(ev) => setTexto(ev.target.value)}
            placeholder={t('oficinaV2.accion.tuRespuesta')}
            className="min-h-10"
          />
          <Boton variante="primario" tamano="md" disabled={!texto.trim()} cargando={enviando} onClick={() => responder({ texto: texto.trim() })}>
            {t('oficinaV2.accion.responder')}
          </Boton>
        </div>
      ) : null}
      {error ? <Aviso tono="error">{error}</Aviso> : null}
    </div>
  );
  return (
    <TarjetaAbierta icono={MessageCircleQuestion} titulo={p.pregunta} pie={pie}>
      {detalle}
    </TarjetaAbierta>
  );
}


// ── Cierre atascado ──


// ── Mensajes que no le llegaron (revisión R4) ──

function SinEntregar({ contratado: c, expediente: e }: { contratado: ContratadoDTO; expediente: ExpedienteDTO }) {
  const { enviando, error, hacer } = useAccion(c.agentId);
  return (
    <Tarjeta icono={TriangleAlert} tono="aviso" titulo={t('oficinaV2.accion.sinEntregar')}>
      {e.sinEntregar.map((m) => (
        <div key={m.messageId} className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-o-texto text-oficina-texto2">
            {m.clase === 'encargo' ? t('oficinaV2.accion.sinEntregarEncargo') : m.clase === 'orden' ? t('oficinaV2.accion.sinEntregarOrden') : m.texto}
          </span>
          {m.reenviable ? (
            <Boton tamano="sm" icono={RotateCcw} cargando={enviando} onClick={() => void hacer(() => oficinaApi.reenviar(c.agentId, m.messageId))}>
              {t('oficinaV2.accion.reenviar')}
            </Boton>
          ) : (
            <span className="shrink-0 text-o-sub text-oficina-texto3">{t('oficinaV2.accion.yaNoAplica')}</span>
          )}
        </div>
      ))}
      {error ? <Aviso tono="error">{error}</Aviso> : null}
    </Tarjeta>
  );
}
